// SectionNav.js — left-rail, random-access section list that defines the
// quote workspace. Each section shows its label, value summary, and a derived
// last-edited stamp. Click any section to focus it (no stepper, no order).
// Sticky. Faithful port of components/workspace/SectionNav.jsx.
//
// Dot color note: the design-system source used off-token indicator hexes
// (#22C55E / #F59E0B). design-system-binding §1 forbids hexes outside the v3
// token group, so the dots map to the nearest v3 status tokens here. Flagged
// for Shaun — revert to brighter indicator hexes only on his sign-off.
import React from 'react';
import { v3 } from '../../lib/styleTokens';

const DOT = {
  ok: v3.color.status.greenText,
  warn: v3.color.status.amberText,
  empty: v3.color.faint,
};

export function SectionNav({ sections = [], activeId, onSelect, title = 'Sections · jump to any' }) {
  return (
    <nav style={{ position: 'sticky', top: v3.space.xxl, fontFamily: v3.font.family }}>
      <div
        style={{
          fontSize: v3.font.size.micro,
          color: v3.color.muted,
          fontWeight: v3.font.weight.semibold,
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          padding: '4px 12px 8px',
        }}
      >
        {title}
      </div>
      <div
        style={{
          background: v3.color.card,
          border: `1px solid ${v3.color.border}`,
          borderRadius: v3.radius.cardLarge,
          padding: 4,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        {sections.map((s) => {
          const active = s.id === activeId;
          return (
            <button
              key={s.id}
              onClick={() => onSelect && onSelect(s.id)}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 10,
                padding: '10px 12px',
                borderRadius: v3.radius.input,
                background: active ? v3.color.inkBg : 'transparent',
                border: `1px solid ${active ? v3.color.inkBorder : 'transparent'}`,
                cursor: 'pointer',
                textAlign: 'left',
                width: '100%',
                font: 'inherit',
              }}
            >
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  marginTop: 6,
                  flex: '0 0 8px',
                  background: active ? v3.color.ink : DOT[s.status] || DOT.ok,
                }}
              />
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                  <span
                    style={{
                      fontSize: v3.font.size.label,
                      fontWeight: active ? v3.font.weight.semibold : v3.font.weight.medium,
                      color: active ? v3.color.ink : v3.color.body,
                    }}
                  >
                    {s.label}
                  </span>
                  <span style={{ flex: 1 }} />
                  {s.ts && (
                    <span style={{ fontSize: v3.font.size.micro, color: v3.color.mutedSoft, fontFamily: v3.font.mono }}>
                      {s.ts}
                    </span>
                  )}
                </span>
                {s.value && (
                  <span
                    style={{
                      display: 'block',
                      fontSize: v3.font.size.small,
                      color: v3.color.muted,
                      marginTop: 2,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {s.value}
                  </span>
                )}
              </span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}
